import { numberFormatterLocal } from '../utils/formatters';

export interface CurrencyFormatterOptions {
  /** Whether to avoid fractions in the output */
  avoidTrailingZero?: boolean;
  /** The currency code to use for formatting */
  currency: string;
  /** Hide currency flag */
  hideCurrency?: boolean;
}

interface IException {
  [key: string]: { original: string; replacement: string };
}

/**
 * Exception currencies for which we replace the currency symbol with a custom one.
 */
const EXCEPTIONS: IException = {
  COP: { original: '$', replacement: 'COP' },
  VES: { original: 'VES', replacement: 'Bs' },
  TWD: { original: '$', replacement: 'NT$' },
  TTD: { original: '$', replacement: 'TT$' },
  BTC6: { original: 'BTC6', replacement: 'μBTC' },
  BTC3: { original: 'BTC3', replacement: 'mBTC' },
  XAF: { original: 'FCFA', replacement: 'XAF' },
  GHS: { original: 'GH₵', replacement: 'GH¢' },
  RSD: { original: 'RSD', replacement: 'Дин.' },
  UZS: { original: 'UZS', replacement: 'лв' },
  KRW2: { original: 'KRW2', replacement: '₩' },
  INR1: { original: 'INR1', replacement: '₹' },
  KES: { original: 'KES', replacement: 'Ksh' },
  IDR1: { original: 'IDR1', replacement: 'Rp' },
  IDR2: { original: 'IDR2', replacement: 'Rp' },
  PEN: { original: 'PEN', replacement: 'S/.' },
  USDT: { original: 'USDT', replacement: '$' },
  ZMW: { original: 'ZK', replacement: 'ZMW' },
  BGN: { original: 'BGN', replacement: 'лв' },
  ETH: { original: 'ETH', replacement: 'Ξ' },
  ETH4: { original: 'ETH4', replacement: 'Ξ' },
  ETH7: { original: 'ETH7', replacement: 'Ξ' },
  ETH9: { original: 'ETH9', replacement: 'Ξ' },
  KZT: { original: '₸', replacement: 'лв' },
  VND2: { original: 'VND2', replacement: '₫' },
};

interface IRuleException {
  [key: string]: Intl.NumberFormatOptions;
}

/**
 * Exception currencies for which some specific formatting rules need to be applied
 */
const RULES_EXCEPTIONS: IRuleException = {
  RSD: { minimumFractionDigits: 2 }, // Somehow Intl counts there are no fractions for RSD, but there ARE by ISO
};

class CurrencyFormatterClass {
  private static onlyNumbersAndPunctuation: RegExp = /[^\d\u0660-\u066C\u06F0-\u06F9\p{P}   _]+/gu;
  /**
   * Cache is used to store the Intl.NumberFormat objects so we don't have to create them every time we need to format a number.
   */
  private static cache: Record<string, Intl.NumberFormat> = {};
  private static customCurrencies: Record<string, boolean> = {};

  private formatCurrency = (value: number, locale: string, options: CurrencyFormatterOptions): string => {
    const currencyTag = [options.currency, locale, Boolean(options.hideCurrency), Boolean(options.avoidTrailingZero)]
      // eslint-disable-next-line prettier/prettier
      .join('_');

    if (!CurrencyFormatterClass.cache[currencyTag]) {
      const ruleExceptions = RULES_EXCEPTIONS[options.currency] || {};
      // Writing to Cache
      CurrencyFormatterClass.cache[currencyTag] = new Intl.NumberFormat(numberFormatterLocal(locale), {
        style: 'currency',
        currency: options.currency,
        currencyDisplay: options.hideCurrency ? 'code' : 'narrowSymbol',
        minimumFractionDigits: options.avoidTrailingZero ? 0 : undefined,
        ...ruleExceptions,
      });
    }

    let formatter = CurrencyFormatterClass.cache[currencyTag].format;

    if (options.hideCurrency) {
      formatter = (val: number): string =>
        CurrencyFormatterClass.cache[currencyTag]
          .format(val)
          .replace(CurrencyFormatterClass.onlyNumbersAndPunctuation, '')
          .trim();
    } else if (EXCEPTIONS[options.currency]) {
      formatter = (val: number): string =>
        CurrencyFormatterClass.cache[currencyTag]
          .format(val)
          .replace(EXCEPTIONS[options.currency].original, EXCEPTIONS[options.currency].replacement);
    }
    return this.negativeZeroSave(value, formatter);
  };

  private negativeZeroSave = (value: number, formatter: (value: number) => string): string => {
    /*
     Safeguard for near zero values
     $0, -$0, $0.0001, -$0.00001 => $0

     !!! signDisplay: "negative" is not supported in earlier browser versions, that's why we use this
    * */
    const minusZero = formatter(-0);
    const actualValue = formatter(value);
    if (minusZero === actualValue) {
      return formatter(0);
    } else {
      return actualValue;
    }
  };

  private formatNumber = (value: number, locale: string, options: CurrencyFormatterOptions): string => {
    const currencyTag = ['decimal', locale, Boolean(options.avoidTrailingZero)].join('_');

    // Check if currency is in the cache, if not - add it
    if (!CurrencyFormatterClass.cache[currencyTag]) {
      CurrencyFormatterClass.cache[currencyTag] = new Intl.NumberFormat(numberFormatterLocal(locale), {
        style: 'decimal',
        minimumFractionDigits: options.avoidTrailingZero ? 0 : 2,
        /**
         * For currencies that can only be formatted as decimals we set maximumFractionDigits to 5.
         * This will deal with any leftover decimals from javascript rounding issues.
         */
        maximumFractionDigits: 5,
      });
    }

    const currency = EXCEPTIONS[options.currency]?.replacement || options.currency;
    return `${options.hideCurrency ? '' : `${currency} `}${CurrencyFormatterClass.cache[currencyTag].format(value)}`;
  };

  public format(value: number, locale: string, options: CurrencyFormatterOptions): string {
    // Check if the currency is already in the custom currency list
    if (!CurrencyFormatterClass.customCurrencies[options.currency]) {
      try {
        return this.formatCurrency(value, locale, options);
      } catch (error) {
        /**
         * Case where the currency is not supported by the Intl.NumberFormat.format() function and must be formatted as a decimal.
         * Adds this to the custom currency list with the value = true, so we don't try to format it as a currency again.
         */
        CurrencyFormatterClass.customCurrencies[options.currency] = true;
      }
    }
    // If it can't be formatted as a currency - format it as a decimal.
    return this.formatNumber(value, locale, options);
  }
}

export const CurrencyFormatter = new CurrencyFormatterClass();
